import { useEffect, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Check,
  Download,
  FileText,
  MessageCircle,
  PencilLine,
  Plus,
  Users,
  AlertCircle,
  Save,
} from 'lucide-react';
import { api, date, downloadRecord, statusLabel, words } from './api';
import { Back, type Navigate } from './App';
import { Empty, ErrorNotice, Modal, Spinner, TopicIcon } from './components';
import { RecordDetails } from './records';
import type { Lesson, Profile, Session, TeacherData } from './types';

export function Teacher({ profile, navigate }: { profile: Profile; navigate: Navigate }) {
  const [data, setData] = useState<TeacherData | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('work');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState<Lesson | 'new' | null>(null);
  const load = () => {
    setError('');
    api<TeacherData>('/teacher/overview')
      .then(setData)
      .catch((cause) => setError(cause.message));
  };
  useEffect(load, []);
  if (!data)
    return (
      <div className="page">
        {error ? <ErrorNotice message={error} retry={load} /> : <Spinner />}
      </div>
    );
  const finished = data.sessions.filter((session) => session.status === 'complete');
  const needsReview = finished.filter((session) => !session.feedback);
  const rows = data.sessions.filter(
    (session) =>
      filter === 'all' ||
      (filter === 'review'
        ? session.status === 'complete' && !session.feedback
        : filter === 'attention'
          ? session.needsAttention
          : session.status !== 'complete'),
  );
  return (
    <div className="page teacher-page">
      <div className="page-heading">
        <div>
          <div className="eyebrow">TEACHER WORKSPACE</div>
          <h1>The thinking behind the work.</h1>
          <p className="page-intro">
            See how students develop an idea, where they need help and what they can do
            independently.
          </p>
        </div>
        <button className="primary" onClick={() => setEditing('new')}>
          <Plus size={18} />
          New assignment
        </button>
      </div>
      <div className="class-strip">
        <span>
          <Users size={18} />
          <strong>{profile.className}</strong>
          <span className="muted">{data.students.length} fictional student profiles</span>
        </span>
        <span className="tag">KS3 SCIENCE</span>
      </div>
      <div className="stats-grid four">
        <div className="stat">
          <Users />
          <strong>
            {new Set(data.sessions.map((session) => session.learnerId)).size}
            <small> / {data.students.length}</small>
          </strong>
          <span>Students who have started</span>
        </div>
        <div className="stat">
          <BookOpen />
          <strong>{data.sessions.filter((session) => session.status !== 'complete').length}</strong>
          <span>Lessons in progress</span>
        </div>
        <div className="stat">
          <Check />
          <strong>{finished.length}</strong>
          <span>Independent checks saved</span>
        </div>
        <div className="stat">
          <MessageCircle />
          <strong>{needsReview.length}</strong>
          <span>Ready for your feedback</span>
        </div>
      </div>
      <div className="teacher-tabs" role="group" aria-label="Teacher area">
        <button className={tab === 'work' ? 'active' : ''} onClick={() => setTab('work')}>
          <PencilLine size={17} />
          Student work
        </button>
        <button
          className={tab === 'assignments' ? 'active' : ''}
          onClick={() => setTab('assignments')}
        >
          <BookOpen size={17} />
          Assignments <span>{data.assignments.length}</span>
        </button>
      </div>
      {tab === 'work' ? (
        <section className="panel teacher-work">
          <div className="section-heading">
            <div>
              <h2>Learning in progress</h2>
              <p>Open a record to see the work, the help and the independent thinking.</p>
            </div>
            <select
              aria-label="Filter student work"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            >
              <option value="all">All work</option>
              <option value="active">In progress</option>
              <option value="review">Awaiting feedback</option>
              <option value="attention">Needs attention</option>
            </select>
          </div>
          {!rows.length ? (
            <Empty
              title={
                data.sessions.length ? 'Nothing in this view yet' : 'Ready when your students are'
              }
            >
              {data.sessions.length
                ? 'Choose another filter to see more work.'
                : 'Switch to a student pilot profile and start an investigation. Their actual work will appear here.'}
            </Empty>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Student & investigation</th>
                    <th>Stage</th>
                    <th>Evidence</th>
                    <th>Marvin help</th>
                    <th>Updated</th>
                    <th>
                      <span className="sr-only">Review</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((session) => {
                    const student = data.students.find((item) => item.id === session.learnerId);
                    return (
                      <tr key={session.id}>
                        <td>
                          <div className="student-cell">
                            <span className="avatar">{student?.initials}</span>
                            <span>
                              <strong>{student?.name}</strong>
                              <small>{session.title}</small>
                              {session.needsAttention && (
                                <span className="attention-label">
                                  <AlertCircle size={12} />
                                  Safety concern · needs adult review
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`stage-badge ${session.status}`}>
                            {statusLabel(session.status)}
                          </span>
                          {session.feedback && <small className="teal-text">Feedback saved</small>}
                        </td>
                        <td>
                          {session.sourceCount} sources
                          <small>{session.words} words</small>
                        </td>
                        <td>{session.assistanceCount} responses</td>
                        <td>{date(session.updatedAt)}</td>
                        <td>
                          <button
                            className="small-button"
                            onClick={() => void navigate(`/review/${session.id}`)}
                          >
                            Review <ArrowRight size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className="panel assignment-list">
          <div className="section-heading">
            <div>
              <h2>Your assignments</h2>
              <p>Published assignments appear for every student in the pilot class.</p>
            </div>
          </div>
          {data.assignments.map((lesson) => (
            <div className="assignment-row" key={lesson.id}>
              <span className={`topic-square ${lesson.colour}`}>
                <TopicIcon topic={lesson.icon} />
              </span>
              <div>
                <strong>{lesson.title}</strong>
                <small>
                  {lesson.yearGroup} · {lesson.subject} ·{' '}
                  {
                    {
                      hints: 'Hints only',
                      explanations: 'Hints & explanations',
                      examples: 'Hints, explanations & examples',
                    }[lesson.assistance]
                  }
                </small>
              </div>
              <span className={`stage-badge ${lesson.published ? 'complete' : ''}`}>
                {lesson.published ? 'Published' : 'Draft'}
              </span>
              <button className="small-button" onClick={() => setEditing(lesson)}>
                <PencilLine size={15} />
                Edit
              </button>
            </div>
          ))}
        </section>
      )}
      <p className="teacher-footnote">
        Records support your judgement. Marvin does not grade pupils or certify authorship.
        Pilot readings need your subject review before classroom use.
      </p>
      {editing && (
        <AssignmentEditor
          initial={editing === 'new' ? undefined : editing}
          templates={data.templates}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            setTab('assignments');
          }}
        />
      )}
    </div>
  );
}

function AssignmentEditor({
  initial,
  templates,
  onClose,
  onSaved,
}: {
  initial?: Lesson;
  templates: TeacherData['templates'];
  onClose: () => void;
  onSaved: () => void;
}) {
  const first = templates.find((item) => item.id === initial?.templateId) || templates[0];
  const [form, setForm] = useState({
    templateId: initial?.templateId || first.id,
    title: initial?.title || first.title,
    instructions: initial?.instructions || first.instructions,
    yearGroup: initial?.yearGroup || first.yearGroup,
    assistance: initial?.assistance || 'examples',
    published: initial?.published ?? false,
    dueDate: initial?.dueDate || '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = templates.find((item) => item.id === form.templateId)!;
  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(
        initial ? `/teacher/assignments/${initial.id}` : '/teacher/assignments',
        initial ? 'PATCH' : 'POST',
        form,
      );
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={initial ? 'Edit assignment' : 'Create a science assignment'}
      onClose={() => {
        if (!busy) onClose();
      }}
      wide
    >
      <form className="assignment-form" onSubmit={(event) => void save(event)}>
        <p className="modal-intro">
          Choose a pilot topic template, then set the task and how much support Marvin may
          offer. Review its readings and independent questions before using it with students.
        </p>
        <details className="template-preview">
          <summary>Review lesson materials & independent questions</summary>
          <div>
            <h3>Learning objectives</h3>
            <ul>
              {selected.objectives.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {selected.sources.map((source) => (
              <section key={source.id}>
                <h3>{source.title}</h3>
                <p>{source.text}</p>
              </section>
            ))}
            <h3>First thoughts</h3>
            <p>{selected.baseline}</p>
            <h3>Independent check</h3>
            <p>{selected.check}</p>
            <h3>Seven-day revisit</h3>
            <p>{selected.revisit}</p>
          </div>
        </details>
        <div className="form-grid">
          <label>
            Topic template
            <select
              value={form.templateId}
              disabled={Boolean(initial) || busy}
              onChange={(event) => {
                const template = templates.find((item) => item.id === event.target.value)!;
                setForm((previous) => ({
                  ...previous,
                  templateId: template.id,
                  title: template.title,
                  instructions: template.instructions,
                  yearGroup: template.yearGroup,
                }));
              }}
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Year group
            <select
              value={form.yearGroup}
              disabled={busy}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  yearGroup: event.target.value,
                }))
              }
            >
              {['Year 7', 'Year 8', 'Year 9'].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Assignment title
          <input
            value={form.title}
            minLength={5}
            maxLength={150}
            required
            disabled={busy}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                title: event.target.value,
              }))
            }
          />
        </label>
        <label>
          Student task
          <textarea
            value={form.instructions}
            minLength={30}
            maxLength={4000}
            rows={5}
            required
            disabled={busy}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                instructions: event.target.value,
              }))
            }
          />
        </label>
        <label>
          Marvin support
          <select
            value={form.assistance}
            disabled={busy}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                assistance: event.target.value as Lesson['assistance'],
              }))
            }
          >
            <option value="hints">Questions and small hints only</option>
            <option value="explanations">Hints and concept explanations</option>
            <option value="examples">Hints, explanations and different examples</option>
          </select>
        </label>
        <div className="form-grid">
          <label>
            Due date <span className="muted">(optional)</span>
            <input
              type="date"
              value={form.dueDate}
              disabled={busy}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  dueDate: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Visibility
            <select
              value={form.published ? 'published' : 'draft'}
              disabled={busy}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  published: event.target.value === 'published',
                }))
              }
            >
              <option value="draft">Draft · teacher only</option>
              <option value="published">Published · visible to the pilot class</option>
            </select>
          </label>
        </div>
        {initial && (
          <p className="subtle-box">
            Changes apply to students who have not started. Existing lessons keep their original
            task and support settings.
          </p>
        )}
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          <button type="button" className="secondary" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy}>
            <Save size={16} />
            {busy ? 'Saving…' : 'Save assignment'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function Review({ id, navigate }: { id: string; navigate: Navigate }) {
  const [session, setSession] = useState<Session | null>(null);
  const [student, setStudent] = useState<Profile | undefined>();
  const [error, setError] = useState('');
  const [tab, setTab] = useState('work');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const load = () => {
    setError('');
    Promise.all([api<Session>(`/sessions/${id}`), api<TeacherData>('/teacher/overview')])
      .then(([work, overview]) => {
        setSession(work);
        setStudent(overview.students.find((item) => item.id === work.learnerId));
        setFeedback(sessionStorage.getItem(`ig-feedback:${id}`) || work.feedback?.text || '');
      })
      .catch((cause) => setError(cause.message));
  };
  useEffect(load, [id]);
  async function saveFeedback(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setSession(
        await api<Session>(`/teacher/sessions/${id}/feedback`, 'POST', {
          text: feedback,
        }),
      );
      sessionStorage.removeItem(`ig-feedback:${id}`);
      setSaved(true);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!session)
    return (
      <div className="page">
        <Back navigate={navigate} teacher />
        {error ? <ErrorNotice message={error} retry={load} /> : <Spinner />}
      </div>
    );
  return (
    <div className="page review-page">
      <Back navigate={navigate} teacher />
      <div className="page-heading">
        <div>
          <div className="eyebrow">
            {student?.name.toUpperCase()} · {statusLabel(session.status).toUpperCase()}
          </div>
          <h1>{session.lesson.title}</h1>
          <p className="page-intro">
            See the learner’s contributions, assistance and independent explanations together.
          </p>
        </div>
        <button
          className="secondary"
          onClick={() => void downloadRecord(id).catch((cause) => setError(cause.message))}
        >
          <Download size={17} />
          Download record
        </button>
      </div>
      {session.messages.some((message) => message.kind === 'safety') && (
        <div className="error-notice">
          <AlertCircle size={20} />
          <span>
            A conversation needs adult attention. Review the exchange with Marvin and follow your
            safeguarding process. No external alert has been sent.
          </span>
        </div>
      )}
      <div className="review-grid">
        <section className="panel review-main">
          <div className="work-tabs">
            <button className={tab === 'work' ? 'active' : ''} onClick={() => setTab('work')}>
              <FileText size={16} />
              Student work
            </button>
            <button className={tab === 'checks' ? 'active' : ''} onClick={() => setTab('checks')}>
              <Check size={16} />
              Understanding
            </button>
            <button className={tab === 'record' ? 'active' : ''} onClick={() => setTab('record')}>
              <MessageCircle size={16} />
              Learning record
            </button>
          </div>
          <div className="review-content">
            {tab === 'work' ? (
              <>
                <div className="section-heading">
                  <h2>{session.submittedAt ? 'Submitted explanation' : 'Current draft'}</h2>
                  <span className="muted">{words(session.draft)} words</span>
                </div>
                {session.draft ? (
                  <p className="student-prose">{session.submittedDraft || session.draft}</p>
                ) : (
                  <Empty title="The page is still open">
                    This learner has not saved a draft yet.
                  </Empty>
                )}
                <h3>Supporting evidence</h3>
                {session.lesson.sources
                  .filter((source) => session.sourceIds.includes(source.id))
                  .map((source) => (
                    <div key={source.id} className="evidence-note">
                      <strong>
                        <BookOpen size={15} />
                        {source.title}
                      </strong>
                      <p>{session.notes[source.id] || 'Selected as evidence; no separate note.'}</p>
                    </div>
                  ))}
                {!session.sourceIds.length && (
                  <p className="muted">No supporting resources selected yet.</p>
                )}
                <details className="task-snapshot">
                  <summary>Original task & support settings</summary>
                  <p>{session.lesson.instructions}</p>
                  <p>
                    Marvin support: {session.lesson.assistance}. Snapshot saved{' '}
                    {date(session.createdAt)}.
                  </p>
                </details>
              </>
            ) : tab === 'checks' ? (
              <>
                <h2>Notice how the thinking changes.</h2>
                <p className="muted">
                  These responses are not automatically graded. Look for the ideas the learner can
                  explain and transfer.
                </p>
                <div className="check-comparison">
                  <section>
                    <span className="topic-label">BEFORE THE LESSON</span>
                    <h3>First thoughts</h3>
                    <p className="muted">{session.lesson.baseline}</p>
                    <p className="quote-block">
                      {session.baseline?.answer ||
                        (session.baseline?.skipped ? 'I’m not sure yet.' : 'Not recorded yet.')}
                    </p>
                  </section>
                  <section>
                    <span className="topic-label">AFTER GUIDED PRACTICE</span>
                    <h3>Independent check</h3>
                    <p className="muted">
                      {session.checkQuestion || 'Available after the learner submits.'}
                    </p>
                    <p className="quote-block">{session.check?.answer || 'Not completed yet.'}</p>
                  </section>
                  <section>
                    <span className="topic-label">DELAYED RETRIEVAL</span>
                    <h3>A week later</h3>
                    <p className="muted">
                      {session.revisitQuestion ||
                        (session.revisitDue
                          ? `Opens ${date(session.revisitDue)}`
                          : 'Scheduled after the independent check.')}
                    </p>
                    {session.revisit && <p className="quote-block">{session.revisit.answer}</p>}
                  </section>
                </div>
              </>
            ) : (
              <RecordDetails session={session} />
            )}
          </div>
        </section>
        <aside className="review-sidebar">
          <section className="panel feedback-panel">
            <span className="large-icon">
              <MessageCircle size={22} />
            </span>
            <h2>A useful next step</h2>
            <p className="muted">
              Leave specific feedback on the learner’s reasoning. They’ll see it alongside their
              work.
            </p>
            <form onSubmit={(event) => void saveFeedback(event)}>
              <label htmlFor="teacher-feedback">Your feedback</label>
              <textarea
                id="teacher-feedback"
                rows={7}
                value={feedback}
                minLength={5}
                maxLength={5000}
                required
                disabled={busy}
                placeholder="You made a useful connection between… Next, try explaining…"
                onChange={(event) => {
                  setFeedback(event.target.value);
                  setSaved(false);
                  sessionStorage.setItem(`ig-feedback:${id}`, event.target.value);
                }}
              />
              {error && <ErrorNotice message={error} />}
              <button className="primary" disabled={busy} type="submit">
                <Check size={16} />
                {busy ? 'Saving…' : 'Save feedback'}
              </button>
              {saved && (
                <p className="save-confirmation" role="status">
                  Feedback saved for the student.
                </p>
              )}
            </form>
          </section>
          <section className="review-reminder">
            <BookOpen size={20} />
            <h3>Evidence, with context.</h3>
            <p>
              The record documents work inside this tool. It supports your judgement and does not
              certify who created work outside it.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
